/**
 * PendingEmailsAccordion - Grouped view of all pending emails to send
 *
 * Features:
 * - Aggregates all work items with email actions
 * - Groups by email template type
 * - Allows individual or batch .eml generation
 * - Opens emails in default email client (Outlook)
 */

import React, { useMemo, useState } from 'react';
import {
  Mail,
  Send,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { getTypeConfig, WORK_ITEM_TYPES } from './pipelineConfig';

/**
 * Email template metadata for display
 */
const EMAIL_TEMPLATES = {
  PIPELINE_INSURANCE_MISSING: {
    label: 'Demande assurance',
    recipient: 'Courtier',
    color: 'red'
  },
  PIPELINE_CT_REMINDER: {
    label: 'RDV contrôle technique',
    recipient: 'Garage / Centre CT',
    color: 'amber'
  },
  PIPELINE_LICENSE_REMINDER: {
    label: 'Rappel permis',
    recipient: 'Conducteur',
    color: 'purple'
  },
  PIPELINE_MAINTENANCE_REMINDER: {
    label: 'Relance maintenance',
    recipient: 'Garage',
    color: 'emerald'
  }
};

/**
 * Get email actions from work items
 */
function extractEmailActions(items) {
  const emailActions = [];

  for (const item of items) {
    const typeConfig = getTypeConfig(item.type);
    const emailAction = typeConfig.actions?.find(a => a.type === 'email');

    if (emailAction) {
      emailActions.push({
        item,
        template: emailAction.template,
        actionLabel: emailAction.label,
        templateMeta: EMAIL_TEMPLATES[emailAction.template] || {
          label: emailAction.label,
          recipient: 'Destinataire',
          color: 'gray'
        }
      });
    }
  }

  return emailActions;
}

/**
 * Group email actions by template
 */
function groupByTemplate(emailActions) {
  const groups = {};

  for (const action of emailActions) {
    const key = action.template;
    if (!groups[key]) {
      groups[key] = {
        template: key,
        meta: action.templateMeta,
        items: []
      };
    }
    groups[key].items.push(action);
  }

  return Object.values(groups);
}

/**
 * Email Item Row
 */
function EmailItemRow({ action, onSend, sending }) {
  const { item, templateMeta } = action;

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
      {/* Priority indicator */}
      <div className={`w-1.5 h-8 rounded-full ${
        item.priority === 'P0' ? 'bg-red-500' :
        item.priority === 'P1' ? 'bg-amber-500' : 'bg-blue-500'
      }`} />

      {/* Item info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 text-sm truncate">
          {item.title}
        </p>
        <p className="text-xs text-gray-500">
          {templateMeta.recipient}
        </p>
      </div>

      {/* Send button */}
      <button
        onClick={() => onSend(action)}
        disabled={sending}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
        title="Générer et ouvrir dans Outlook"
      >
        {sending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ExternalLink className="w-4 h-4" />
        )}
        Envoyer
      </button>
    </div>
  );
}

/**
 * Template Group
 */
function TemplateGroup({ group, expanded, onToggle, onSend, onSendAll, sendingId }) {
  const colorClasses = {
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700'
  };

  const colorClass = colorClasses[group.meta.color] || colorClasses.gray;

  return (
    <div className="border rounded-lg overflow-hidden mb-2 last:mb-0">
      {/* Header */}
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 py-3 ${colorClass} border-b`}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        <Mail className="w-4 h-4" />
        <span className="font-medium flex-1 text-left">
          {group.meta.label}
        </span>
        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-white">
          {group.items.length}
        </span>
      </button>

      {/* Items */}
      {expanded && (
        <div className="bg-white">
          {group.items.map(action => (
            <EmailItemRow
              key={action.item.id}
              action={action}
              onSend={onSend}
              sending={sendingId === action.item.id}
            />
          ))}

          {/* Batch send */}
          {group.items.length > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t">
              <button
                onClick={() => onSendAll(group.items)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Générer tous ({group.items.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * PendingEmailsAccordion component
 */
function PendingEmailsAccordion({ items = [], onEmail }) {
  // All accordions CLOSED by default (project-wide constant)
  const [expanded, setExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sendingId, setSendingId] = useState(null);

  // Extract and group email actions
  const emailActions = useMemo(() => extractEmailActions(items), [items]);
  const groupedEmails = useMemo(() => groupByTemplate(emailActions), [emailActions]);

  // Initialize expanded state for groups - all CLOSED by default
  React.useEffect(() => {
    const initial = {};
    groupedEmails.forEach(group => {
      initial[group.template] = false; // All closed by default
    });
    setExpandedGroups(initial);
  }, [groupedEmails.length]); // eslint-disable-line

  // No emails to send
  if (emailActions.length === 0) {
    return null;
  }

  const toggleGroup = (template) => {
    setExpandedGroups(prev => ({
      ...prev,
      [template]: !prev[template]
    }));
  };

  const handleSend = async (action) => {
    setSendingId(action.item.id);
    try {
      await onEmail(action.item, action.template);
    } finally {
      setSendingId(null);
    }
  };

  const handleSendAll = async (actions) => {
    // Generate all emails sequentially
    for (const action of actions) {
      setSendingId(action.item.id);
      try {
        await onEmail(action.item, action.template);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      } finally {
        setSendingId(null);
      }
    }
  };

  return (
    <div className="mb-6">
      {/* Main accordion header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-blue-600" />
        ) : (
          <ChevronRight className="w-5 h-5 text-blue-600" />
        )}
        <Send className="w-5 h-5 text-blue-600" />
        <span className="font-semibold text-blue-900 flex-1 text-left">
          Emails à envoyer
        </span>
        <span className="px-3 py-1 text-sm font-bold text-blue-700 bg-white rounded-full border border-blue-200">
          {emailActions.length}
        </span>
      </button>

      {/* Content */}
      {expanded && (
        <div className="mt-3 pl-4 border-l-2 border-blue-200">
          {groupedEmails.map(group => (
            <TemplateGroup
              key={group.template}
              group={group}
              expanded={expandedGroups[group.template]}
              onToggle={() => toggleGroup(group.template)}
              onSend={handleSend}
              onSendAll={handleSendAll}
              sendingId={sendingId}
            />
          ))}

          {/* Help text */}
          <p className="mt-3 text-xs text-gray-500 italic">
            Cliquez sur "Envoyer" pour générer le fichier .eml et l'ouvrir dans Outlook.
          </p>
        </div>
      )}
    </div>
  );
}

export default PendingEmailsAccordion;
